import {
    ButtonGradientPrimary,
    Colors,
    ConfirmDialog,
    Icon,
    SelectFilled,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';

import { BackLink } from '@/components/BackLink';
import { fullName } from '@/components/CompetencyProfileAssignment/helpers';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

type ChoiceStepProps = {
    employee: RosterEmployee;
    // Other employees who have a profile, available as copy sources.
    copyCandidates: RosterEmployee[];
    onSelectExisting: () => void;
    onCreateGuided: () => void;
    onCopyFrom: (sourceEmployeeId: string) => void;
    onCancel: () => void;
    /** When false, the "Create profile" card is disabled with an explanatory tooltip. */
    canManageCompetencies: boolean;
};

// The per-employee "choice" step shown for an employee WITHOUT a profile: pick an
// existing profile, create a guided one (reuses our wizard), or copy another
// employee's profile.
export const ChoiceStep = ({
    employee,
    copyCandidates,
    onSelectExisting,
    onCreateGuided,
    onCopyFrom,
    onCancel,
    canManageCompetencies,
}: ChoiceStepProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const [showCopy, setShowCopy] = useState(false);
    const [copyFromId, setCopyFromId] = useState('');
    const [showNoPermissionModal, setShowNoPermissionModal] = useState(false);

    const name = useMemo(() => fullName(employee), [employee]);

    // Copy-source options: each candidate labelled with their assigned profile so the
    // admin sees which profile they'd copy. Feeds the modern SelectFilled dropdown.
    const copyOptions = useMemo(
        () =>
            copyCandidates.map((candidate) => {
                const profileName = candidate.assignedProfileName
                    ? getTextByLanguage(candidate.assignedProfileName, language)
                    : '';

                return {
                    value: candidate.id,
                    label: profileName
                        ? `${fullName(candidate)} – ${profileName}`
                        : fullName(candidate),
                };
            }),
        [copyCandidates, language],
    );

    const cards: Array<{
        key: string;
        icon: 'users-user-profile-add-2' | 'essential-add';
        // Distinct gradient per card (matches the prototype): purple for "pick existing",
        // teal for "create guided". White icon on top of the gradient tile.
        iconTileClassName: string;
        title: string;
        description: string;
        onClick: () => void;
        testId: string;
        /** Visual-only lock: card appears restricted but click fires onClick (shows modal). */
        locked?: boolean;
    }> = [
        {
            key: 'existing',
            icon: 'users-user-profile-add-2',
            iconTileClassName: 'bg-gradient-to-br from-purple-500 to-purple-700',
            title: t('competencies.competencyProfilesAdmin.choice.selectExisting'),
            description: t('competencies.competencyProfilesAdmin.choice.selectExistingDescription'),
            onClick: onSelectExisting,
            testId: 'roster-choice-existing',
        },
        {
            key: 'guided',
            icon: 'essential-add',
            iconTileClassName: 'bg-gradient-to-br from-primary to-teal-600',
            title: t('competencies.competencyProfilesAdmin.choice.createGuided'),
            description: t('competencies.competencyProfilesAdmin.choice.createGuidedDescription'),
            onClick: canManageCompetencies ? onCreateGuided : () => setShowNoPermissionModal(true),
            testId: 'roster-choice-guided',
            locked: !canManageCompetencies,
        },
    ];

    return (
        <>
            <BackLink
                label={t('competencies.competencyProfilesAdmin.back')}
                onClick={onCancel}
                className={'self-start'}
                dataTestId={'roster-choice-cancel'}
            />

            <div>
                <p className={'text-navy text-2xl font-medium sm:text-3xl'}>
                    {t('competencies.competencyProfilesAdmin.choice.title')}
                </p>

                <p className={'text-navy/70 mt-2 text-lg'}>
                    {t('competencies.competencyProfilesAdmin.choice.subtitle', { name })}
                </p>
            </div>

            <div className={'mx-auto flex w-full max-w-[868px] flex-col gap-6'}>
                <div className={'responsive-grid-1-2 gap-6'}>
                    {cards.map((card) => (
                        <TooltipProvider key={card.key}>
                            <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                    <span className={'block h-full'}>
                                        <button
                                            type={'button'}
                                            onClick={card.onClick}
                                            className={[
                                                'group relative flex h-full w-full flex-col gap-4 rounded-xl border border-white bg-white/80 p-6 text-left backdrop-blur-sm transition-all sm:p-10',
                                                card.locked
                                                    ? 'cursor-pointer opacity-60'
                                                    : 'hover:shadow-lg',
                                            ].join(' ')}
                                            data-testid={card.testId}
                                        >
                                            {card.locked && (
                                                <span
                                                    className={
                                                        'absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-gray-500'
                                                    }
                                                >
                                                    <Icon
                                                        name={'security-lock'}
                                                        size={'sm'}
                                                        color={'inherit'}
                                                    />
                                                </span>
                                            )}

                                            <span
                                                className={
                                                    'flex h-16 w-16 items-center justify-center rounded-xl ' +
                                                    card.iconTileClassName
                                                }
                                            >
                                                <Icon
                                                    name={card.icon}
                                                    size={'lg'}
                                                    color={Colors.WHITE}
                                                />
                                            </span>

                                            <span className={'mt-3 flex flex-col gap-2'}>
                                                <span
                                                    className={
                                                        'text-navy text-[20px] font-semibold transition-colors group-hover:text-teal-500'
                                                    }
                                                >
                                                    {card.title}
                                                </span>

                                                <span className={'text-[16px] text-gray-500'}>
                                                    {card.description}
                                                </span>
                                            </span>
                                        </button>
                                    </span>
                                </TooltipTrigger>

                                {card.locked && (
                                    <TooltipContent
                                        side={'top'}
                                        className={'max-w-[280px]'}
                                    >
                                        <p>
                                            {t(
                                                'competencies.competencyProfilesAdmin.noCreatePermissionTooltip',
                                            )}
                                        </p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    ))}
                </div>

                <div className={'mt-2 text-center'}>
                    <button
                        type={'button'}
                        onClick={() => setShowCopy((value) => !value)}
                        className={
                            'text-navy/60 hover:text-primary text-sm underline transition-colors'
                        }
                        data-testid={'roster-choice-copy-toggle'}
                    >
                        {t('competencies.competencyProfilesAdmin.choice.copyFromEmployee')}
                    </button>
                </div>

                {showCopy && (
                    <div
                        className={
                            'mt-4 rounded-xl border border-white bg-white/80 p-6 backdrop-blur-sm sm:p-[24px]'
                        }
                    >
                        <p className={'text-navy mb-2 text-[18px] font-semibold'}>
                            {t('competencies.competencyProfilesAdmin.choice.copyTitle')}
                        </p>

                        {copyCandidates.length === 0 ? (
                            <p className={'text-md text-gray-500'}>
                                {t('competencies.competencyProfilesAdmin.choice.copyEmpty')}
                            </p>
                        ) : (
                            <>
                                <p className={'text-md mb-3 text-gray-500'}>
                                    {t('competencies.competencyProfilesAdmin.choice.copyHelper', {
                                        name,
                                    })}
                                </p>

                                <div className={'max-w-md'}>
                                    <SelectFilled
                                        value={copyFromId}
                                        options={copyOptions}
                                        placement={'bottom-left'}
                                        onChange={(value: string) => setCopyFromId(value)}
                                        variant={'white'}
                                        triggerBorderClassName={'border border-gray-200'}
                                        className={'[&>span]:!text-navy [&>span]:!font-normal'}
                                        placeholder={t(
                                            'competencies.competencyProfilesAdmin.choice.copyPlaceholder',
                                        )}
                                        dataTestId={'roster-choice-copy-select'}
                                    />
                                </div>

                                <div className={'mt-4 flex justify-end'}>
                                    <ButtonGradientPrimary
                                        size={'md'}
                                        disabled={!copyFromId}
                                        onClick={() => copyFromId && onCopyFrom(copyFromId)}
                                        dataTestId={'roster-choice-copy-confirm'}
                                    >
                                        {t(
                                            'competencies.competencyProfilesAdmin.choice.copyConfirm',
                                        )}
                                    </ButtonGradientPrimary>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <ConfirmDialog
                isOpen={showNoPermissionModal}
                title={t('competencies.competencyProfilesAdmin.noCreatePermissionTitle')}
                message={t('competencies.competencyProfilesAdmin.noCreatePermissionTooltip')}
                confirmLabel={t('common.close')}
                onConfirm={() => setShowNoPermissionModal(false)}
                onCancel={() => setShowNoPermissionModal(false)}
                hideCancel
                dataTestId={'choice-no-permission-modal'}
            />
        </>
    );
};
